import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

interface CampaignPeriod {
  period: string; periodStart: string; periodEnd: string;
  impressions: number; clicks: number;
  spend: number; sales: number; orders: number;
  acos: number; ctr: number; cpc: number; cvr: number; costPerOrder: number;
}

interface SearchTermPeriod {
  period: string; periodStart: string; periodEnd: string;
  uniqueTerms: number; impressions: number; clicks: number;
  spend: number; sales: number; orders: number;
  acos: number; ctr: number; cpc: number; cvr: number;
}

interface TopTerm {
  searchTerm: string; impressions: number; clicks: number;
  spend: number; sales: number; orders: number; acos: number; cpc: number;
}

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;
const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n);
const pct = (n: number) => `${n.toFixed(1)}%`;

function acosColor(val: number): string {
  if (val <= 0) return '';
  if (val < 20) return 'bg-green-50 text-green-800';
  if (val < 40) return 'bg-yellow-50 text-yellow-800';
  return 'bg-red-50 text-red-800';
}

function fmtPeriodLabel(d: { period: string; periodStart: string; periodEnd: string }): string {
  const shortDate = (iso: string) => {
    const [, m, day] = iso.split('-');
    return `${parseInt(m)}/${parseInt(day)}`;
  };
  if (!d.periodStart || !d.periodEnd) return d.period.replace(/^\d{4}-/, '');
  const range = d.periodStart === d.periodEnd
    ? shortDate(d.periodStart)
    : `${shortDate(d.periodStart)}-${shortDate(d.periodEnd)}`;
  // Monthly: "Mar (3/1-3/29)", Weekly: "W13 (3/24-3/30)"
  const label = d.period.replace(/^\d{4}-/, '');
  return `${label}\n${range}`;
}

type Tab = 'campaign' | 'searchTerm' | 'topTerms';

const CAMPAIGN_METRICS: { key: keyof CampaignPeriod; label: string; format: (v: number) => string; colorFn?: (v: number) => string }[] = [
  { key: 'spend', label: 'PPC Spend', format: fmtUsd },
  { key: 'sales', label: 'PPC Sales', format: fmtUsd },
  { key: 'orders', label: 'Orders', format: fmtNum },
  { key: 'impressions', label: 'Impressions', format: fmtNum },
  { key: 'clicks', label: 'Clicks', format: fmtNum },
  { key: 'acos', label: 'ACoS', format: pct, colorFn: acosColor },
  { key: 'ctr', label: 'CTR', format: pct },
  { key: 'cpc', label: 'CPC', format: fmtUsd },
  { key: 'cvr', label: 'Conversion', format: pct },
  { key: 'costPerOrder', label: 'Cost/Order', format: fmtUsd },
];

const ST_METRICS: { key: keyof SearchTermPeriod; label: string; format: (v: number) => string; colorFn?: (v: number) => string }[] = [
  { key: 'uniqueTerms', label: 'Unique Terms', format: fmtNum },
  { key: 'spend', label: 'Spend', format: fmtUsd },
  { key: 'sales', label: 'Sales', format: fmtUsd },
  { key: 'orders', label: 'Orders', format: fmtNum },
  { key: 'impressions', label: 'Impressions', format: fmtNum },
  { key: 'clicks', label: 'Clicks', format: fmtNum },
  { key: 'acos', label: 'ACoS', format: pct, colorFn: acosColor },
  { key: 'ctr', label: 'CTR', format: pct },
  { key: 'cpc', label: 'CPC', format: fmtUsd },
  { key: 'cvr', label: 'Conversion', format: pct },
];

export function PpcTrends() {
  const [tab, setTab] = useState<Tab>('campaign');
  const [view, setView] = useState<'weekly' | 'monthly'>('weekly');
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [campaignData, setCampaignData] = useState<CampaignPeriod[]>([]);
  const [stData, setStData] = useState<SearchTermPeriod[]>([]);
  const [topTerms, setTopTerms] = useState<TopTerm[]>([]);

  useEffect(() => {
    setLoading(true);
    api.get<{ campaignPeriods: CampaignPeriod[]; searchTermPeriods: SearchTermPeriod[]; topTerms: TopTerm[] }>(
      `/ppc/search-term-trend?view=${view}&days=${days}`,
    ).then((r) => {
      setCampaignData(r.campaignPeriods);
      setStData(r.searchTermPeriods);
      setTopTerms(r.topTerms);
    }).finally(() => setLoading(false));
  }, [view, days]);

  const TABS: { key: Tab; label: string }[] = [
    { key: 'campaign', label: 'Campaign Trend' },
    { key: 'searchTerm', label: 'Search Term Trend' },
    { key: 'topTerms', label: 'Top Search Terms' },
  ];

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">PPC Trends</h1>
        <span className="text-xs text-gray-400">All amounts in local currency</span>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1">
          {(['weekly', 'monthly'] as const).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1 text-sm rounded ${view === v ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
              {v === 'weekly' ? 'Weekly' : 'Monthly'}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {[30, 60, 90, 180].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-1 text-sm rounded ${days === d ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b mb-4">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`pb-2 text-sm font-medium border-b-2 transition ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-gray-400 text-sm">Loading...</p>}

      {/* Campaign Trend Table */}
      {tab === 'campaign' && campaignData.length > 0 && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 z-10 min-w-[120px]">Metric</th>
                {campaignData.map((d) => (
                  <th key={d.period} className="text-right px-3 py-2 min-w-[90px] whitespace-pre-line leading-tight">
                    {fmtPeriodLabel(d)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAMPAIGN_METRICS.map((row) => (
                <tr key={row.key} className="border-b hover:bg-gray-50/50">
                  <td className="px-3 py-1.5 font-medium text-gray-700 sticky left-0 bg-white z-10">{row.label}</td>
                  {campaignData.map((d) => {
                    const val = d[row.key] as number;
                    const bg = row.colorFn ? row.colorFn(val) : '';
                    return (
                      <td key={d.period} className={`px-3 py-1.5 text-right tabular-nums ${bg}`}>
                        {row.format(val)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Search Term Trend Table */}
      {tab === 'searchTerm' && stData.length > 0 && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 z-10 min-w-[120px]">Metric</th>
                {stData.map((d) => (
                  <th key={d.period} className="text-right px-3 py-2 min-w-[90px] whitespace-pre-line leading-tight">
                    {fmtPeriodLabel(d)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ST_METRICS.map((row) => (
                <tr key={row.key} className="border-b hover:bg-gray-50/50">
                  <td className="px-3 py-1.5 font-medium text-gray-700 sticky left-0 bg-white z-10">{row.label}</td>
                  {stData.map((d) => {
                    const val = d[row.key] as number;
                    const bg = row.colorFn ? row.colorFn(val) : '';
                    return (
                      <td key={d.period} className={`px-3 py-1.5 text-right tabular-nums ${bg}`}>
                        {row.format(val)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Search Terms */}
      {tab === 'topTerms' && topTerms.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2">Search Term</th>
                <th className="text-right px-3 py-2">Impressions</th>
                <th className="text-right px-3 py-2">Clicks</th>
                <th className="text-right px-3 py-2">Spend</th>
                <th className="text-right px-3 py-2">Sales</th>
                <th className="text-right px-3 py-2">Orders</th>
                <th className="text-right px-3 py-2">ACoS</th>
                <th className="text-right px-3 py-2">CPC</th>
              </tr>
            </thead>
            <tbody>
              {topTerms.map((t) => (
                <tr key={t.searchTerm} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-1.5 font-medium max-w-[250px] truncate">{t.searchTerm}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(t.impressions)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(t.clicks)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtUsd(t.spend)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtUsd(t.sales)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(t.orders)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${acosColor(t.acos)}`}>{pct(t.acos)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtUsd(t.cpc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
